import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import { Plus, UserPlus, Shield, Users, GraduationCap, Edit2, Trash2, KeyRound, Search } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({ username: "", password: "" });
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "accountant",
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/create-user`, formData);
      toast.success("User created successfully");
      setIsModalOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to create user");
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      role: "accountant",
    });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditData({ username: user.username, password: "" });
    setEditModalOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editData.username !== selectedUser.username) payload.username = editData.username;
      if (editData.password) payload.password = editData.password;
      if (Object.keys(payload).length === 0) {
        toast.info("No changes made");
        return;
      }
      await axios.put(`${API}/users/${selectedUser.id}`, payload);
      toast.success("User updated successfully");
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <Shield size={14} className="text-pink-500" />;
      case "teacher":
        return <Users size={14} className="text-blue-500" />;
      case "student":
        return <GraduationCap size={14} className="text-green-500" />;
      case "accountant":
        return <KeyRound size={14} className="text-amber-500" />;
      default:
        return null;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-pink-50 text-pink-700 hover:bg-pink-50";
      case "teacher":
        return "bg-blue-50 text-blue-700 hover:bg-blue-50";
      case "student":
        return "bg-green-50 text-green-700 hover:bg-green-50";
      case "accountant":
        return "bg-amber-50 text-amber-700 hover:bg-amber-50";
      default:
        return "bg-slate-50 text-slate-700";
    }
  };

  const filteredUsers = searchQuery
    ? users.filter((u) =>
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.role.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const adminUsers = users.filter((u) => u.role === "admin");
  const teacherUsers = users.filter((u) => u.role === "teacher");
  const studentUsers = users.filter((u) => u.role === "student");
  const accountantUsers = users.filter((u) => u.role === "accountant");

  return (
    <div data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
            <UserPlus className="text-pink-500" size={28} />
            User Management
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Create and manage school user accounts
          </p>
        </div>

        <Dialog open={isModalOpen} onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-pink-500 hover:bg-pink-600 text-white btn-press"
              data-testid="create-user-btn"
            >
              <Plus size={18} className="mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Create User</DialogTitle>
              <DialogDescription>
                Create a new account for admin, accountant, teacher, or student access.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Enter username"
                  required
                  data-testid="user-username"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter password"
                  required
                  data-testid="user-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <select
                  className="w-full border border-slate-300 rounded-md h-10 px-3 bg-white"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="user-role"
                >
                  <option value="accountant">Accountant</option>
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-pink-500 hover:bg-pink-600"
                  data-testid="save-user-btn"
                >
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="text-pink-500" size={20} />
              <div>
                <p className="text-2xl font-bold text-slate-900">{adminUsers.length}</p>
                <p className="text-sm text-slate-500">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="text-blue-500" size={20} />
              <div>
                <p className="text-2xl font-bold text-slate-900">{teacherUsers.length}</p>
                <p className="text-sm text-slate-500">Teachers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="text-green-500" size={20} />
              <div>
                <p className="text-2xl font-bold text-slate-900">{studentUsers.length}</p>
                <p className="text-sm text-slate-500">Students</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <KeyRound className="text-amber-500" size={20} />
              <div>
                <p className="text-2xl font-bold text-slate-900">{accountantUsers.length}</p>
                <p className="text-sm text-slate-500">Accountants</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="font-heading text-lg">
              All Users ({filteredUsers.length})
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 text-sm border-slate-200"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <UserPlus size={48} className="mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? "No users match your search" : "No users found"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Username</TableHead>
                    <TableHead className="font-medium text-slate-500">Role</TableHead>
                    <TableHead className="font-medium text-slate-500">Created</TableHead>
                    <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="table-row-hover"
                      data-testid={`user-row-${user.id}`}
                    >
                      <TableCell className="font-medium text-slate-900">
                        {user.username}
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeColor(user.role)}>
                          <span className="flex items-center gap-1">
                            {getRoleIcon(user.role)}
                            {user.role}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600"
                            onClick={() => openEditModal(user)}
                            title="Edit user"
                          >
                            <Edit2 size={15} />
                          </Button>
                          {currentUser?.id !== user.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                              onClick={() => { setSelectedUser(user); setDeleteDialogOpen(true); }}
                              title="Delete user"
                            >
                              <Trash2 size={15} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editModalOpen} onOpenChange={(open) => {
        setEditModalOpen(open);
        if (!open) { setSelectedUser(null); setEditData({ username: "", password: "" }); }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound size={18} className="text-blue-500" />
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update username or reset password for <strong>{selectedUser?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={editData.username}
                onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={editData.password}
                onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                placeholder="Leave blank to keep current password"
              />
              <p className="text-xs text-slate-400">Minimum 6 characters. Leave empty to keep unchanged.</p>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-slate-50 text-sm">
                {getRoleIcon(selectedUser?.role)}
                <span className="capitalize">{selectedUser?.role}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.username}</strong>?
              This will also remove their associated student or teacher records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
